import { Documentation_PageMetadataKey, usePermissions } from '@tyro/api';
import { useEffect, useMemo } from 'react';
import { useReaderState } from '../store/documentation-reader-provider';
import { gateMarkdownByAccess } from '../utils/block-access';
import { useDocumentationPreviewEditor } from '../utils/markdown-editor';
import { getPageMetaValue } from '../utils/page-metadata';
import { useRecentlyViewedDocs } from './use-recently-viewed-docs';

/**
 * View-model for `ReaderContent`, derived from the provider-resolved
 * `currentPageDetail` — so it runs no fetch of its own — and records the
 * "recently viewed" entry. Kind-neutral: `isVideo` lets the caller branch.
 */
export function useReaderPage() {
  const {
    currentSpace,
    location,
    currentPageDetail,
    isLoadingPage: isLoading,
  } = useReaderState();

  const page = currentPageDetail?.page ?? null;
  const metadata = currentPageDetail?.metadata;

  const spaceSlug = currentSpace?.slug ?? '';
  const pageSlug = location.view === 'page' ? location.pageSlug : '';
  const spaceName = currentSpace?.name ?? '';

  const {
    hasPermission,
    userType,
    permissions: userPermissions,
  } = usePermissions();

  const isDocsAdmin = hasPermission('api:documentation:admin');

  const isVideo = page?.entityType === 'video';
  const quality = getPageMetaValue(
    metadata,
    Documentation_PageMetadataKey.Quality,
  );

  const { recordView } = useRecentlyViewedDocs();

  useEffect(() => {
    if (!page || !spaceName) return;
    recordView({
      pageId: page.id,
      pageSlug: page.slug,
      title: page.title,
      entityType: page.entityType,
      spaceId: page.spaceId,
      spaceSlug,
      spaceName,
    });
  }, [page, spaceSlug, spaceName, recordView]);

  // Articles run permission/userType block filtering. Video descriptions are
  // rendered raw — block-level gating doesn't apply to them.
  const bodyMarkdown = useMemo(() => {
    if (!page?.contentMd) return '';
    if (isVideo) return page.contentMd;
    return gateMarkdownByAccess(page.contentMd, metadata, {
      userPermissions,
      userType,
    });
  }, [page?.contentMd, isVideo, metadata, userPermissions, userType]);

  const editor = useDocumentationPreviewEditor(bodyMarkdown);

  return {
    page,
    metadata,
    isLoading,
    isVideo,
    quality,
    isDocsAdmin,
    bodyMarkdown,
    editor,
    spaceSlug,
    pageSlug,
  };
}
